// ============================================================================
// User Preferences Context
// Feature: 035-app-shell-enhancement
// Author: Peter Dobler
// Created: 2026-01-21
// Description: React context provider for user preferences management.
//              Handles localStorage persistence with fallback to session
//              defaults when storage is unavailable.
// ============================================================================

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
  UserPreferences,
  UserPreferencesContextValue,
  DEFAULT_USER_PREFERENCES,
  STORAGE_KEYS,
} from '../types/shell';

// =============================================================================
// Context Creation
// =============================================================================

const UserPreferencesContext = createContext<UserPreferencesContextValue | undefined>(undefined);

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Check if localStorage is available
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Load preferences from localStorage with session fallback
 */
const loadPreferences = (): UserPreferences => {
  // First try localStorage
  if (isLocalStorageAvailable()) {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<UserPreferences>;
        // Merge with defaults to ensure all fields exist
        return {
          ...DEFAULT_USER_PREFERENCES,
          ...parsed,
        };
      }

      // Check for legacy theme key and migrate
      const legacyTheme = localStorage.getItem(STORAGE_KEYS.THEME);
      if (legacyTheme && ['light', 'dark', 'system'].includes(legacyTheme)) {
        return {
          ...DEFAULT_USER_PREFERENCES,
          theme: legacyTheme as UserPreferences['theme'],
        };
      }
    } catch (error) {
      console.warn('Failed to load preferences from localStorage:', error);
    }
  }

  // Fallback to sessionStorage if localStorage is unavailable
  try {
    const sessionStored = sessionStorage.getItem(STORAGE_KEYS.PREFERENCES);
    if (sessionStored) {
      const parsed = JSON.parse(sessionStored) as Partial<UserPreferences>;
      return {
        ...DEFAULT_USER_PREFERENCES,
        ...parsed,
      };
    }
  } catch (error) {
    console.warn('Failed to load preferences from sessionStorage:', error);
  }

  return { ...DEFAULT_USER_PREFERENCES };
};

/**
 * Save preferences to localStorage with session fallback
 */
const savePreferences = (preferences: UserPreferences): void => {
  // Primary storage: localStorage
  if (isLocalStorageAvailable()) {
    try {
      localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(preferences));
      // Also sync theme to legacy key for ThemeProvider compatibility
      localStorage.setItem(STORAGE_KEYS.THEME, preferences.theme);
      return; // Success, no need for fallback
    } catch (error) {
      console.warn('Failed to save preferences to localStorage:', error);
    }
  }

  // Fallback storage: sessionStorage (persists for browser session)
  try {
    sessionStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(preferences));
    console.info('Preferences saved to sessionStorage as localStorage fallback');
  } catch (error) {
    console.warn('Failed to save preferences to sessionStorage:', error);
    // At this point, preferences will only persist in memory for the component lifecycle
  }
};

// =============================================================================
// Provider Component
// =============================================================================

export interface UserPreferencesProviderProps {
  children: React.ReactNode;
  /** Override initial preferences (for testing) */
  initialPreferences?: UserPreferences;
}

/**
 * UserPreferencesProvider - Provides user preferences context to the app
 *
 * @remarks
 * This provider handles loading/saving preferences from localStorage with
 * graceful fallback when storage is unavailable. It also syncs the theme
 * preference with the legacy ThemeProvider key.
 *
 * @example
 * ```tsx
 * <UserPreferencesProvider>
 *   <App />
 * </UserPreferencesProvider>
 * ```
 */
export const UserPreferencesProvider: React.FC<UserPreferencesProviderProps> = ({
  children,
  initialPreferences,
}) => {
  const [isStorageAvailable] = useState(() => isLocalStorageAvailable());
  const [preferences, setPreferencesState] = useState<UserPreferences>(() => {
    if (initialPreferences) {
      return initialPreferences;
    }
    return loadPreferences();
  });

  // Save to localStorage whenever preferences change
  useEffect(() => {
    if (!initialPreferences) {
      savePreferences(preferences);
    }
  }, [preferences, initialPreferences]);

  // Warn user if localStorage is unavailable
  useEffect(() => {
    if (!isStorageAvailable) {
      console.warn(
        'localStorage is not available. User preferences will not persist across sessions.'
      );
    }
  }, [isStorageAvailable]);

  /**
   * Update a single preference key
   */
  const updatePreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      setPreferencesState((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  /**
   * Update multiple preferences at once
   */
  const setPreferences = useCallback((newPreferences: Partial<UserPreferences>) => {
    setPreferencesState((prev) => ({
      ...prev,
      ...newPreferences,
    }));
  }, []);

  /**
   * Reset all preferences to defaults
   */
  const resetPreferences = useCallback(() => {
    setPreferencesState({ ...DEFAULT_USER_PREFERENCES });
  }, []);

  /**
   * Add a path to favorites
   */
  const addFavorite = useCallback((path: string, title?: string, category?: string) => {
    setPreferencesState((prev) => {
      if (prev.favorites.some(f => f.path === path)) {
        return prev;
      }
      const newFavorite = {
        id: crypto.randomUUID(),
        path,
        title: title || path,
        category: category || 'Other',
        addedAt: new Date().toISOString(),
      };
      return {
        ...prev,
        favorites: [...prev.favorites, newFavorite],
      };
    });
  }, []);

  /**
   * Remove a path from favorites
   */
  const removeFavorite = useCallback((path: string) => {
    setPreferencesState((prev) => ({
      ...prev,
      favorites: prev.favorites.filter((f) => f.path !== path),
    }));
  }, []);

  /**
   * Check if a path is in favorites
   */
  const isFavorite = useCallback(
    (path: string): boolean => {
      return preferences.favorites.some(f => f.path === path);
    },
    [preferences.favorites]
  );

  const contextValue = useMemo<UserPreferencesContextValue>(
    () => ({
      preferences,
      updatePreference,
      setPreferences,
      resetPreferences,
      addFavorite,
      removeFavorite,
      isFavorite,
      isStorageAvailable,
    }),
    [
      preferences,
      updatePreference,
      setPreferences,
      resetPreferences,
      addFavorite,
      removeFavorite,
      isFavorite,
      isStorageAvailable,
    ]
  );

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

// =============================================================================
// Hook
// =============================================================================

/**
 * useUserPreferencesContext - Access user preferences from context
 *
 * @throws Error if used outside of UserPreferencesProvider
 *
 * @example
 * ```tsx
 * const { preferences, updatePreference } = useUserPreferencesContext();
 * ```
 */
export const useUserPreferencesContext = (): UserPreferencesContextValue => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferencesContext must be used within a UserPreferencesProvider');
  }
  return context;
};

export default UserPreferencesContext;
